import React from 'react';

import {
  isParagraphElement,
  isRenderModelElement,
  isSignatureFieldElement,
  Select,
} from '@aline/common';
import type { RenderModelElement } from '@aline/common';
import type { NodeEntry } from 'slate';
import { Queries } from 'src/editor/queries';
import { IS_CYPRESS } from 'src/helpers/environment';

import { useEditor, useHeaderOrFooterOpen } from '../hooks';

// In development, temporarily set this to true to debug bounding boxes
const ENABLE_DEBUG_OVERLAY = /* true || */ IS_CYPRESS;

// Invisible in Cypress
const OVERLAY_OPACITY = IS_CYPRESS ? 0 : 0.25;

/**
 * Render an overlay showing the positions of all bounding boxes.
 *
 * This is currently used as part of some legacy Cypress tests to locate elements such as signature
 * fields. Once these Cypress tests have been migrated to Playwright, this component should remain
 * only for the purpose of debugging bounding boxes in development.
 *
 * Do not use this overlay in Playwright tests, since Playwright is equipped with a much more
 * sophisticated Slate adapter that can be used to write tests that are simple and reliable.
 */
export function DebugOverlay({
  rMElementEntry,
}: {
  rMElementEntry: NodeEntry<RenderModelElement>;
}) {
  const editor = useEditor();
  const isHeaderOpen = useHeaderOrFooterOpen('header');
  const isFooterOpen = useHeaderOrFooterOpen('footer');

  if (!ENABLE_DEBUG_OVERLAY) return null;

  const boundingBoxes = Queries.getRMBoundingBoxes(editor, rMElementEntry, {
    isFooterOpen,
    isHeaderOpen,
  });

  return boundingBoxes.map((boundingBox, index) => {
    const { heightPx, leftPx, topPx, type, widthPx } = boundingBox;

    const rMNodeEntry = type === 'node' ? boundingBox.rMNodeEntry : null;
    const [rMNode, rMNodePath] = rMNodeEntry ?? [null, null];
    const rMElement = rMNode && isRenderModelElement(rMNode) ? rMNode : null;

    // Used in Cypress only. Remove when no longer needed.
    const signatureField = rMNode && isSignatureFieldElement(rMNode) ? rMNode : null;
    const isSignatureField = !!signatureField;
    const isSignedSignatureField = !!signatureField?.signatureImgId;
    const isParagraph = !!rMNode && isParagraphElement(rMNode);

    return (
      <div
        key={index}
        data-bounding-box-type={rMNode ? (rMElement?.type ?? 'text') : type}
        data-cy={
          isSignedSignatureField
            ? Select.document.debugOverlay.signedSignatureField
            : isSignatureField
              ? Select.document.debugOverlay.signatureField
              : isParagraph
                ? Select.document.debugOverlay.paragraph
                : undefined
        }
        data-node-json={rMNode ? JSON.stringify(rMNode) : undefined}
        style={{
          backgroundColor: 'red',
          height: heightPx,
          left: leftPx,
          opacity: OVERLAY_OPACITY,
          position: 'absolute',
          top: topPx,
          width: widthPx,
        }}
      >
        {rMElement && rMNodePath && <DebugOverlay rMElementEntry={[rMElement, rMNodePath]} />}
      </div>
    );
  });
}
